import { useEffect, useState } from "react";
import {
  BodyText,
  Card,
  Flex,
  Heading,
  RadioGroup,
  RadioTile,
  Button,
  CardContent,
} from "@utilitywarehouse/hearth-react";
import { useNavigate } from "react-router-dom";

import { PATHS } from "../../config/routeConfig";
import { useVulnerabilityContext } from "../../context/vulnerability/useVulnerabilityContext";
import { checkIfPermanentNeeds, checkIfTemporaryNeedsEmpty } from "../utils";
import { checkTokenExpiryOnInterval } from "../../context/auth/Token";
import Unauthenticated from "../../components/Unauthenticated";
import { useAuthContext } from "../../context/auth/useAuthContext";
import useTrackVulnerabilityJourney from "../../middleware/analytics/hooks/useTrackVulnerabilityJourney";

import { OptForPsrDataTest } from "./constants";

export enum PsrOptAnswer {
  Yes = "Yes",
  No = "No",
  None = "None",
}


const OptForPSR: React.FC = () => {
  const { answers, setAnswers } = useVulnerabilityContext();
  const { trackVulnerabilityJourneyOpen } = useTrackVulnerabilityJourney();
  const { setAuthenticated, authenticated } = useAuthContext();
  const navigate = useNavigate();
  const [optForPSR, setOptForPSR] = useState<PsrOptAnswer>(PsrOptAnswer.None);

  const noTemporaryNeeds = checkIfTemporaryNeedsEmpty(answers);
  const hasPermanentNeedCode = checkIfPermanentNeeds(answers);

  useEffect(() => {
    trackVulnerabilityJourneyOpen(PATHS.OptForPSR, `1 of 3`);
  }, []);

   const handleChange = (value: string): void => {
    switch (value) {
      case PsrOptAnswer.Yes:
        setAnswers({
          ...answers,
          psr: {
            psrOutcome: {
              eligible: true,
              optedIn: true,
            },
            psrTemporaryNeedsCodes: undefined,
          },
        });

        setOptForPSR(PsrOptAnswer.Yes);
        return;

      case PsrOptAnswer.No:
        setAnswers({
          ...answers,
          psr: {
            psrOutcome: {
              eligible: true,
              optedIn: false,
            },
            psrTemporaryNeedsCodes: undefined,
          },
        });

        setOptForPSR(PsrOptAnswer.No);
        return;

      case PsrOptAnswer.None:
      default:
        setOptForPSR(PsrOptAnswer.None);
        return;
    }
  };

  const handleNavigate = (): void => {
    switch (optForPSR) {
      case PsrOptAnswer.Yes:
        if (noTemporaryNeeds) {
          navigate(`/${PATHS.PSRServices}`);
        } else {
          navigate(`/${PATHS.TemporaryNeeds}`);
        }
        return;

      case PsrOptAnswer.No:
        navigate(`/${PATHS.PSRSummary}`);
        return;

      case PsrOptAnswer.None:
      default:
        return;
    }
  };

  const renderButtonText = (): string => {
    switch (optForPSR) {
      case PsrOptAnswer.Yes:
        return "Get started to be added to PSR";

      case PsrOptAnswer.No:
        return "Submit answer and update your account";

      case PsrOptAnswer.None:
      default:
        return "Select an option";
    }
  };

  useEffect(() => {
    checkTokenExpiryOnInterval(authenticated, setAuthenticated);
  }, []);

  return (
    <Flex direction="column" marginTop="150">
      {!authenticated ? (
        <Unauthenticated />
      ) : (
        <Flex direction="column" spacing="lg">
          <Heading size="lg" data-testid={OptForPsrDataTest.PSR_TITLE}>
            {hasPermanentNeedCode
              ? "You’re eligible for Priority Services Register"
              : "You may be eligible for Priority Services Register"}
          </Heading>

          <BodyText>
            Your answers suggest you’re eligible for the
            <b> Priority Services Register</b>, which entitles you to additional
            support services related to your energy services.
          </BodyText>

          <BodyText>
            To get more information about what you could be entitled to answer
            the below:
          </BodyText>

          <Card>
            <CardContent direction="column" gap="175" width="fit-content">
              <BodyText>
                This will take approx <b>5mins</b> to complete. There will be no
                opportunity to save your answers throughout so please make sure
                you have the time to complete the full form.
              </BodyText>

              <RadioGroup
                aria-label="confirm psr-selection"
                name="confirm-psr-selection-buttons-group"
                onValueChange={handleChange}
                value={optForPSR}
              >
                <RadioTile
                  label="Yes, I want to be on the PSR"
                  value={PsrOptAnswer.Yes}
                  data-testid={OptForPsrDataTest.YES_BTN}
                />
                <RadioTile
                  label="No, I don't want to be on the PSR"
                  value={PsrOptAnswer.No}
                  data-testid={OptForPsrDataTest.NO_BTN}
                />
              </RadioGroup>

              <Button
                disabled={optForPSR === PsrOptAnswer.None}
                onClick={handleNavigate}
                data-testid={OptForPsrDataTest.CONTINUE_OR_SUBMIT_BUTTON}
                style={{ maxWidth: "fit-content" }}
              >
                {renderButtonText()}
              </Button>
            </CardContent>
          </Card>
        </Flex>
      )}
    </Flex>
  );
};

export default OptForPSR;
